import { ChartjsTooltip } from '../../../../components';
import { getSelectedLocationName, getSelectedLocations, getSelectedSubjectName, getSelectedSubjects, getUrlQueryParams, getSubjectColor, getAvgScoreForGroup, getGroupAveragesForSubjectForGraph, getGroupCodeFromName, getSubjectTitle, isCitvoiceMeasure, getSpecificValue, roundToOne, isACountry, extractValuePairs, getSelectedYears, getLocationNameMarkup, calculateTrendNumbers, possibleException, isException } from '../../../../actions';
import { formatNumberForDisplay } from '../../../../components/DataTable/utils';
import { chartDataIterator } from '../RangeChart/utils';
import { regionCodes, defaultVariables, baseFont, trendClassificationColors } from '../../../../referenceData';
import i18n from '../../../../i18n';
import TitleWithNames from '../../../../components/NameTitle/index.jsx';
import getSelectedGroupHighlight from '../../../../actions/getSelectedGroupHighlight';
import getCountriesInGroup from '../../../../actions/getCountriesInGroup';

export const isMultipleSubjectSelected = (subjects) => subjects.length > 1;

const getGroupAveragesForSubjectForYearArray = (groupCode, subject, years, state) => {
  // Put relevant data in Processing subset
  const averages = [];
  years.forEach((year) => {
    const pos = {};
    let sc = roundToOne(getAvgScoreForGroup(state, subject, year, groupCode));
    sc = isNaN(sc) ? -1 : sc;
    pos[year] = sc;
    averages.push(pos);
  });

  return averages;
};

const getAvarageValuesForSelectedYears = (data, selectedYears) => {
  const averageValues = [];
  data.values.forEach((value) => (selectedYears.includes(value.x) ? averageValues.push(value.y) : null));
  return averageValues;
};

const getAfricanAvarageStats = (state, subject, year) => {
  const avgData = chartDataIterator(state, getGroupAveragesForSubjectForGraph(state, getGroupCodeFromName(state, 'Africa'), subject), i18n.t('African average'), 'dashed', '#8e8e8e', false, false, 10, 1);

  return getAvarageValuesForSelectedYears(avgData, year);
};

const getDataForMultipleLocations = (state, subject, locations, selectedYears, groupHighlight, showClassification, highlightedTrend, selectedYearsRange2) => {
  const data = locations.map((location) => {
    const fullLocationName = getSelectedLocationName(state, location);
    const fullSubjectName = getSelectedSubjectName(state, subject);
    const subjectColor = getSubjectColor({ state, subject });

    const countriesInGroup = [];
    if (groupHighlight) {
      countriesInGroup.push(...getCountriesInGroup(state, groupHighlight.uid).map((c) => c.iso));
    }

    let scoreChangeRange1;
    let trendClassification = null;
    let colour = subjectColor;

    if (!isACountry(location)) {
      const valTrends = extractValuePairs(getGroupAveragesForSubjectForYearArray(location, subject, selectedYears, state));
      const first = valTrends.shift().value;
      const last = valTrends.pop().value;
      scoreChangeRange1 = first !== -1 && last !== -1 ? roundToOne(last - first) : 'n/a';
    } else {
      const startValue1 = roundToOne(getSpecificValue(state, location, subject, selectedYears[0]));
      const endValue1 = roundToOne(getSpecificValue(state, location, subject, selectedYears[selectedYears.length - 1]));

      scoreChangeRange1 = endValue1 !== -1 && startValue1 !== -1 ? roundToOne(endValue1 - startValue1) : 'n/a';

      // Calculate trend classification if enabled
      if (showClassification && scoreChangeRange1 !== 'n/a') {
        // Get values for range2 from URL params (same as ATScatterChart)
        const startValue2 = roundToOne(getSpecificValue(state, location, subject, selectedYearsRange2[0]));
        const endValue2 = roundToOne(getSpecificValue(state, location, subject, selectedYearsRange2[selectedYearsRange2.length - 1]));

        let exception = false;
        if (possibleException(endValue2)) {
          exception = isException(roundToOne(getSpecificValue(state, location, subject, defaultVariables.latest_year - 3)), roundToOne(getSpecificValue(state, location, subject, defaultVariables.latest_year - 2)), roundToOne(getSpecificValue(state, location, subject, defaultVariables.latest_year - 1)), endValue2);
        }

        const results = calculateTrendNumbers(startValue1, endValue1, startValue2, endValue2, selectedYears, selectedYearsRange2, exception);
        trendClassification = results.classifText;

        // Apply classification color (trendClassificationColors are already in hex format)
        colour = trendClassificationColors[results.classifIndex];
      } else if (!showClassification && groupHighlight && !countriesInGroup.includes(location)) {
        colour = subjectColor + '44';
      }
    }

    return {
      key: fullLocationName,
      value: scoreChangeRange1,
      props: {
        color: colour,
        location: {
          fullName: fullLocationName,
          abr: location,
        },
        subject: {
          fullName: fullSubjectName,
          abr: subject,
        },
        trendClassification,
      },
    };
  });

  return data;
};

const getDataForMultipleSubjects = (state, subjects, location, selectedYears, showClassification, highlightedTrend, selectedYearsRange2) => {
  const data = subjects.map((subject) => {
    const fullSubjectName = getSelectedSubjectName(state, subject);
    const fullLocationname = getSelectedLocationName(state, location);

    let scoreChangeRange1;
    let trendClassification = null;
    let colour = isCitvoiceMeasure(state, subject) ? '#A19FA3' : getSubjectColor({ state, subject });

    if (!isACountry(location)) {
      const valTrends = extractValuePairs(getGroupAveragesForSubjectForYearArray(location, subject, selectedYears, state));
      const first = valTrends.shift().value;
      const last = valTrends.pop().value;
      scoreChangeRange1 = first !== -1 && last !== -1 ? roundToOne(last - first) : 'n/a';
    } else {
      const startValue1 = roundToOne(getSpecificValue(state, location, subject, selectedYears[0]));
      const endValue1 = roundToOne(getSpecificValue(state, location, subject, selectedYears[selectedYears.length - 1]));

      scoreChangeRange1 = endValue1 !== -1 && startValue1 !== -1 ? roundToOne(endValue1 - startValue1) : 'n/a';

      // Calculate trend classification if enabled
      if (showClassification && scoreChangeRange1 !== 'n/a') {
        // Get values for range2 from URL params (same as ATScatterChart)
        const startValue2 = roundToOne(getSpecificValue(state, location, subject, selectedYearsRange2[0]));
        const endValue2 = roundToOne(getSpecificValue(state, location, subject, selectedYearsRange2[selectedYearsRange2.length - 1]));

        let exception = false;
        if (possibleException(endValue2)) {
          exception = isException(roundToOne(getSpecificValue(state, location, subject, defaultVariables.latest_year - 3)), roundToOne(getSpecificValue(state, location, subject, defaultVariables.latest_year - 2)), roundToOne(getSpecificValue(state, location, subject, defaultVariables.latest_year - 1)), endValue2);
        }

        const results = calculateTrendNumbers(startValue1, endValue1, startValue2, endValue2, selectedYears, selectedYearsRange2, exception);
        trendClassification = results.classifText;

        // Apply classification color (trendClassificationColors are already in hex format)
        colour = trendClassificationColors[results.classifIndex];
      }
    }

    return {
      key: fullSubjectName,
      value: scoreChangeRange1,
      props: {
        color: colour,
        location: {
          fullName: fullLocationname,
          abr: location,
        },
        subject: {
          fullName: fullSubjectName,
          abr: subject,
        },
        trendClassification,
      },
    };
  });
  return data;
};

const getDatasetsForChart = (state, subjects, locations, selectedYears, groupHighlight, showClassification, highlightedTrend, selectedYearsRange2) => {
  if (isMultipleSubjectSelected(subjects)) {
    return getDataForMultipleSubjects(state, subjects, locations[0], selectedYears, showClassification, highlightedTrend, selectedYearsRange2);
  }
  return getDataForMultipleLocations(state, subjects[0], locations, selectedYears, groupHighlight, showClassification, highlightedTrend, selectedYearsRange2);
};

const getDataForTooltip = (state, subjects, locations) => {
  const body = {};
  const footer = [];

  if (isMultipleSubjectSelected(subjects)) {
    const locationCode = locations[0];
    const location = getSelectedLocationName(state, locationCode);
    const locationMarkup = getLocationNameMarkup({ code: locationCode, name: location });
    subjects.forEach((subject) => {
      footer.push(i18n.t('African average for {{country}}', { country: getSelectedSubjectName(state, subject) }));
      body[getSelectedSubjectName(state, subject)] = {
        abr: subject,
        label: `${getSelectedSubjectName(state, subject)}: ${locationMarkup}`, // eslint-disable-line i18next/no-literal-string
        locationCode,
        locationName: location,
        locationMarkup,
      };
    });
  } else {
    footer.push(i18n.t('African average'));
    locations.forEach((location) => {
      const locationName = getSelectedLocationName(state, location);
      const locationMarkup = getLocationNameMarkup({ code: location, name: locationName });
      body[locationName] = {
        abr: subjects[0],
        label: `${getSelectedSubjectName(
          // eslint-disable-line i18next/no-literal-string
          state,
          subjects[0],
        )}: ${locationMarkup}`,
        locationCode: location,
        locationName,
        locationMarkup,
      };
    });
  }

  return {
    body,
    footer,
  };
};

export const getChartTitle = (state, urlParams) => {
  const years = getSelectedYears('range1from', 'range1to');
  const suffix = ` (${years[0]}-${years[years.length - 1]})`;
  return <TitleWithNames subject={getSubjectTitle(state)} locations={getSelectedLocations(state)} suffix={suffix} />;
};

const sortData = (chartData, urlParams, measureTreeOrder) => {
  const { sortBy, sortDir, meas } = urlParams;
  let sortedData;
  const sortDesc = typeof sortDir === 'undefined' || sortDir === 'des';

  if (typeof sortBy === 'undefined' || sortBy === 'score') {
    sortedData = [...chartData].sort((a, b) => {
      if (b.value === 'n/a') {
        return -1;
      } else if (a.value === 'n/a') {
        return 1;
      } else {
        return sortDesc ? Number(b.value) - Number(a.value) : Number(a.value) - Number(b.value);
      }
    });
    // we return here; this version has to keep the n/a values at the end regardless of sort order
    // so the sort func handles dir, rather than reversing post sort
    return sortedData;
  }

  if ((typeof sortBy !== 'undefined' && sortBy === 'index') || (sortBy === 'name' && isMultipleSubjectSelected(meas.split('-')))) {
    sortedData = [...chartData].sort((a, b) => measureTreeOrder.indexOf(a.props.subject.abr) - measureTreeOrder.indexOf(b.props.subject.abr)).reverse();
  }

  if (typeof sortBy !== 'undefined' && sortBy === 'name') {
    sortedData = [...chartData].sort((a, b) => a.key.localeCompare(b.key));
  }

  return sortDesc ? sortedData.reverse() : sortedData;
};

const createBarChartDatasets = (state, isMultipleSubjectsSelected, data, year) => {
  if (isMultipleSubjectsSelected) {
    return {
      labels: data.map((subject) => subject.key),
      datasets: [
        {
          label: `${data[0].props.location.fullName} ${Number(year[0])}-${Number(year[year.length - 1])}`,
          backgroundColor: data.map((subject) => subject.props.color),
          hoverBackgroundColor: data.map((subject) => subject.props.color),
          data: data.map((subject) => {
            if (subject.value === '-1.0') {
              return NaN;
            }
            return subject.value;
          }),
          trendClassifications: data.map((subject) => subject.props.trendClassification),
          maxBarThickness: 150,
          tooltip: {
            order: 1,
          },
        },
      ],
    };
  }

  const colours = data.map((location) => {
    return location.props.color;
  });

  return {
    labels: data.map((location) => location.key),
    datasets: [
      {
        label: data[0].props.subject.fullName,
        backgroundColor: colours,
        hoverBackgroundColor: colours,
        data: data.map((location) => {
          if (location.value === '-1.0') {
            return NaN;
          }
          return location.value;
        }),
        trendClassifications: data.map((location) => location.props.trendClassification),
        maxBarThickness: 150,
        selectedYear: `${year[0]}-${year[year.length - 1]}`,
        tooltip: {
          order: 1,
        },
      },
    ],
  };
};

export const getChartConfig = (state) => {
  const urlParams = getUrlQueryParams();
  const subjects = getSelectedSubjects(state);
  const locations = getSelectedLocations(state);
  const selectedYears = getSelectedYears('range1from', 'range1to');
  const selectedYearsRange2 = getSelectedYears('range2from', 'range2to');
  const tooltipData = getDataForTooltip(state, subjects, locations);
  const groupHighlight = getSelectedGroupHighlight(state);
  const { measureTreeOrder } = state;
  const showClassification = JSON.parse(urlParams?.showClassifications || false);
  const highlightedTrend = urlParams?.highlightedTrend || null;

  const data = getDatasetsForChart(state, subjects, locations, selectedYears, groupHighlight, showClassification, highlightedTrend, selectedYearsRange2);

  const sortedData = sortData(data, urlParams, measureTreeOrder);

  const fixedNAData = sortedData.map((el) => {
    el.value = el.value == 'n/a' ? null : el.value;
    return el;
  });

  const chartData = createBarChartDatasets(state, isMultipleSubjectSelected(subjects), fixedNAData, selectedYears);

  const indexAxis = window.innerWidth < 768 ? 'y' : 'x';
  const otherAxis = indexAxis === 'y' ? 'x' : 'y';

  return {
    type: 'bar',
    data: chartData,
    options: {
      interaction: {
        intersect: false,
        mode: window.innerWidth > 768 ? 'index' : indexAxis,
        includeInvisible: true,
      },
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          left: 10,
          right: 10,
          bottom: 35,
          top: 30,
        },
      },
      indexAxis,
      scales: {
        [indexAxis]: {
          title: {
            display: locations.length >= 40 || subjects.length >= 40,
            text: subjects.length === 1 ? i18n.t('Locations').toUpperCase() : i18n.t('Measures').toUpperCase(),
            color: 'black',
            font: {
              size: 16,
              family: baseFont(),
              weight: 500,
            },
          },
          grid: {
            display: false,
          },
          ticks: {
            maxRotation: 90,
            callback(val) {
              if (this.ticks.length >= 40) {
                return;
              }
              return this.getLabelForValue(val);
            },
            display: true,
            autoSkip: false,
            barThickness: 30,
            font: {
              size: 12,
              family: baseFont(),
              weight: 500,
            },
            color: 'black',
          },
        },
        [otherAxis]: {
          title: {
            display: true,
            text: i18n.t('Change').toUpperCase(),
            color: 'black',
            font: {
              size: 16,
              family: baseFont(),
              weight: 500,
            },
          },
          suggestedMin: -1,
          suggestedMax: 1,
          ticks: {
            stepSize: 1,
            maxTicksLimit: 7,
            callback: (val) => (val <= 0 ? '' : '+') + formatNumberForDisplay(Number(val), false),
            color: 'black',
            font: {
              size: 16,
              family: baseFont(),
              weight: 500,
            },
          },
          grid: {
            color: (context) => {
              return context.tick.value == 0 ? '#000000' : '#C3C3C3';
            },
            lineWidth: (context) => {
              return context.tick.value == 0 ? 1 : 1;
            },
          },
        },
      },
      plugins: {
        title: {
          display: false,
        },
        legend: {
          display: true,
          position: 'bottom',
          labels: {
            usePointStyle: true,
            generateLabels(chart) {
              const { datasets } = chart.data;
              const {
                labels: { textAlign, color },
              } = chart.legend.options;

              const legend = chart._getSortedDatasetMetas().map((meta) => {
                const style = { ...meta.controller.getStyle(meta.index) };
                if (meta.index === 1) {
                  style.borderDash = [3, 3];
                  style.pointStyle = 'line';
                }
                if ((isMultipleSubjectSelected(subjects) && meta.type === 'line') || meta.type === 'bar') {
                  return null;
                }

                return {
                  text: meta.type === 'bar' ? i18n.t('Range Of Scores') : datasets[meta.index].label,
                  fillStyle: style.backgroundColor,
                  fontColor: color,
                  hidden: isMultipleSubjectSelected(subjects) && meta.type === 'line' ? false : !meta.visible,
                  lineCap: style.borderCapStyle,
                  lineDash: style.borderDash,
                  lineDashOffset: style.borderDashOffset,
                  lineJoin: style.borderJoinStyle,
                  lineWidth: 2,
                  strokeStyle: style.borderColor,
                  pointStyle: style.pointStyle,
                  rotation: style.rotation,
                  textAlign: textAlign || style.textAlign,
                  borderRadius: 0,
                  datasetIndex: meta.index,
                };
              }, this);

              if (groupHighlight) {
                const subjectColor = getSubjectColor({ state, subject: subjects[0] });
                legend.push({
                  text: getSelectedLocationName(state, groupHighlight.uid),
                  fillStyle: subjectColor,
                  fontColor: color,
                  hidden: false,
                  strokeStyle: datasets[0].borderColor,
                  pointStyle: 'circle',
                  borderRadius: 0,
                  datasetIndex: -1,
                });
              }

              return legend.filter((item) => item !== null);
            },
          },
          onClick: function (e, legendItem, legend) {
            const index = legendItem.datasetIndex;
            if (index === -1) {
              return false;
            }
            const ci = legend.chart;
            if (ci.isDatasetVisible(index)) {
              ci.hide(index);
              legendItem.hidden = true;
            } else {
              ci.show(index);
              legendItem.hidden = false;
            }
          },
        },
        tooltip: {
          enabled: false,
          external: (context) =>
            ChartjsTooltip({
              state,
              context,
              tooltipData,
              hideComparison: true,
              activeDatasets: [],
              chartName: 'absoluteTrendBarChart',
              isMultiSubjectView: isMultipleSubjectSelected(subjects),
              alwaysSign: true,
            }),
        },
      },
    },
  };
};
